export type PopperPlacement = 'right' | 'left' | 'bottom' | 'top' | 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start';
export type PopperOnDisplayAnimation = 'shift-toward' | 'shift-away' | 'fade' | 'scale' | 'perspective';

// look at https://atomiks.github.io/tippyjs/ for additional configuration options that you can add.
export interface PoppableElementConfig {
  arrow: boolean;
  placement: PopperPlacement;
  trigger: string;
  performance: boolean;
  zIndex?: number;
  appendTo?: Element | (() => Element);
  sticky?: boolean;
  arrowTransform?: string;
  animation?: PopperOnDisplayAnimation;
  allowTitleHTML?: boolean;
  dynamicTitle?: boolean;
  theme?: string | Element;
  html?: string | Element;
  popperOptions?: any;
  interactive?: boolean;
  duration?: number | Array<number>;
  onShow?: (instance: any) => void;
  onShown?: (instance: any) => void;
  onHidden?: (instance: any) => void;
}
export interface PopperConfigurationIf {
  showArrow: boolean;
  defaultPlacement: PopperPlacement;
  trigger: string; // 'click', 'manual' 'mouseenter focus'
  zIndex?: number; // the z-index of the popper
  appendTo?: Element | (() => Element); // this is not to be confused with the htmlReference property, but this is where the element containing the tooltip gets placed in the dom.
  stickToReference?: boolean;
  arrowSizeTransform?: string; //  // CSS syntax: 'scaleX(0.5)', 'scale(2)', 'translateX(5px)' etc.
  onDisplayAnimation?: PopperOnDisplayAnimation;
  allowHTMLTitle?: boolean; // If true, HTML can be injected in the title attribute
  hasDynamicTitle?: boolean; // If true, whenever the title attribute on the reference changes, the tooltip will automatically be updated
  customStyleClassName?: string; // note custom styles will have -theme appended to it. ex. 'popover' will be styled as .popover-theme
  htmlReference?: string | Element;
  popperJsOptions?: any; // these options should be taken from https://popper.js.org/popper-documentation.html
  interactive?: boolean;
  transitionDuration?: number | Array<number>;
  onHiddenCallback?: (instance: any) => void; // callback invoked when the tooltip begins to transition out.
  onShownCallback?: (instance: any) => void; // callback invoked when the tooltip after the tooltip transitions in.
  beforeShownCallback?: (instance: any) => void; // callback invoked when the tooltip begins to transition in.
}
export class PopperConfiguration {

  static getPopperConfiguration(options: PopperConfigurationIf = {
    showArrow: false,
    defaultPlacement: 'bottom-start',
    trigger: 'mouseenter focus'
  }): PoppableElementConfig {
    //basic configurations
    let configuration: PoppableElementConfig = {
      arrow: options.showArrow,
      placement: options.defaultPlacement,
      trigger: options.trigger,
      performance: true
    };
    PopperConfiguration.enhanceBasePopperConfigurationOption(configuration, options);
    return configuration;
  }

  private static enhanceBasePopperConfigurationOption(configuration: PoppableElementConfig, options: PopperConfigurationIf): void {
    if (options.beforeShownCallback != null) {
      let propName = 'onShow';
      configuration[propName] = options.beforeShownCallback;
    }
    if (options.onShownCallback != null) {
      let propName = 'onShown';
      configuration[propName] = options.onShownCallback;
    }
    if (options.transitionDuration != null) {
      let propName = 'duration';
      configuration[propName] = options.transitionDuration;
    }
    if (options.onHiddenCallback != null) {
      let propName = 'onHidden';
      configuration[propName] = options.onHiddenCallback;
    }
    if (options.stickToReference != null) {
      let propName = 'sticky';
      configuration[propName] = options.stickToReference;
    }
    if (options.zIndex != null) {
      let propName = 'zIndex';
      configuration[propName] = options.zIndex;
    }
    if (options.appendTo != null) {
      let propName = 'appendTo';
      configuration[propName] = options.appendTo;
    }
    if (options.onDisplayAnimation != null) {
      let propName = 'animation';
      configuration[propName] = options.onDisplayAnimation;
    }
    if (options.arrowSizeTransform != null) {
      let propName = 'arrowTransform';
      configuration[propName] = options.arrowSizeTransform;
    }
    if (options.allowHTMLTitle != null) {
      let propName = 'allowTitleHTML';
      configuration[propName] = options.allowHTMLTitle;
    }
    if (options.hasDynamicTitle != null) {
      let propName = 'dynamicTitle';
      configuration[propName] = options.hasDynamicTitle;
    }
    if (options.customStyleClassName != null) {
      let propName = 'theme';
      configuration[propName] = options.customStyleClassName;
    }
    if (options.htmlReference != null) {
      let propName = 'html';
      configuration[propName] = options.htmlReference;
    }
    if (options.popperJsOptions != null) {
      let propName = 'popperOptions';
      configuration[propName] = options.popperJsOptions;
    }
    if (options.interactive != null) {
      let propName = 'interactive';
      configuration[propName] = options.interactive;
    }
  }
}
